import { Component, inject, CUSTOM_ELEMENTS_SCHEMA, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

// Shared Components
import { AppInputOtp } from '../../shared/components/app-input-otp/app-input-otp';
import { AppButton } from '../../shared/components/app-button/app-button';
import { AppAuthHeader } from '../../shared/components/app-auth-header/app-auth-header';
import { AppAuthIntro } from '../../shared/components/app-auth-intro/app-auth-intro';

// Services
import { AuthService } from '../../core/services/auth.service';

@Component({
    selector: 'app-two-factor-auth',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AppInputOtp,
        AppButton,
        AppAuthHeader,
        AppAuthIntro
    ],
    templateUrl: './two-factor-auth.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TwoFactorAuth {
    twoFactorForm: FormGroup;
    loading = false;
    errorMessage = '';
    
    // Almacenar estado para que no se pierda tras el constructor
    private userId?: string;
    private rememberMe = false;

    private fb = inject(FormBuilder);
    private router = inject(Router);
    private authService = inject(AuthService);

    @ViewChild('otpInput') otpInput!: AppInputOtp;

    constructor() {
        this.twoFactorForm = this.fb.group({
            code: ['', [Validators.required, Validators.minLength(6)]]
        });

        // Recuperar estado del login (userId y preferencia de recordar)
        const navigation = this.router.getCurrentNavigation();
        const state = (navigation?.extras?.state || {}) as { user?: string, rememberMe?: boolean };
        
        if (state?.user) {
            this.userId = state.user;
            this.rememberMe = !!state.rememberMe;
        } else {
            // Si no hay estado, regresar al login
            this.router.navigate(['/auth/login']);
        }
    }

    onSubmit() {
        this.verify();
    }

    onCodeComplete(code: string) {
        this.twoFactorForm.patchValue({ code });
        if (code.length > 0) {
            this.errorMessage = ''; // Limpiar error al empezar a escribir
        }
    }

    verify() {
        if (this.twoFactorForm.invalid || !this.userId) return;

        this.loading = true;
        this.errorMessage = '';

        this.authService.authenticate2FA({
            user: this.userId,
            token: this.twoFactorForm.value.code,
            deviceId: localStorage.getItem('dentium_device_id') || '',
            rememberMe: this.rememberMe
        }).pipe(
            finalize(() => this.loading = false)
        ).subscribe({
            next: () => {
                this.router.navigate(['/app/select-business'], { queryParams: { auto: true } });
            },
            error: (err) => {
                // Manejar error de backend o genérico
                const errorData = err.error || err;
                this.errorMessage = errorData.message || (typeof errorData === 'string' ? errorData : 'Código incorrecto. Reinténtalo.');
                
                // Limpiar el campo del código y poner foco para reintento
                this.twoFactorForm.patchValue({ code: '' });
                if (this.otpInput) {
                   this.otpInput.clear();
                }
            }
        });
    }

    handleHelp = () => {
        console.log('Solicitando ayuda con el código 2FA');
    };

    goBack = () => {
        this.router.navigate(['/login']);
    };
}
